import { z } from 'zod';
import type { AppName } from './AppName';
import { type EntitlementName, entitlementNames, entitlementNameSchema } from './EntitlementNames';
import { productNameSchema } from './ProductName';
import { stripeAccountNameSchema } from './StripeAccount';
import { revenueCatSubscriptionSchema, ScheduledSubscription } from './subscriptions';
import { JupiterCustomSubscription } from './subscriptions/JupiterCustomSubscription';
import { PayPalSubscription } from './subscriptions/PaypalSubscription';
import { SaturnCustomSubscription } from './subscriptions/SaturnCustomSubscription';

export const EntitlementToAttributesMap: Record<EntitlementName, { isUnlimited: boolean; appNames: AppName[] }> = {
  gtv_unlimited: { isUnlimited: true, appNames: ['galateatv'] },
  gtv_early_access: { isUnlimited: false, appNames: ['galateatv'] },
  unlimited: { isUnlimited: true, appNames: ['galatea'] },
  subscription: { isUnlimited: true, appNames: ['galatea'] },
  with_ads: { isUnlimited: true, appNames: ['galatea'] },
  unlimited_with_ads: { isUnlimited: true, appNames: ['galatea'] },
  unlimited_app_bundle: { isUnlimited: true, appNames: ['galatea', 'galateatv'] },
  textonly: { isUnlimited: true, appNames: ['galatea'] },
  audioonly: { isUnlimited: true, appNames: ['galatea'] },
  pointsadd: { isUnlimited: false, appNames: ['galatea'] },
  unknown: { isUnlimited: false, appNames: ['galatea', 'galateatv'] },
};

export const EntitlementSources = ['stripe', 'revcat', 'lifetime', 'paypal', 'revcat_simulated', 'custom'] as const;
export type EntitlementSources = (typeof EntitlementSources)[number];

export const GalateaEntitlementSources = ['stripe', 'revcat', 'revcat_simulated', 'paypal', 'lifetime'] as const;
export type GalateaEntitlementSources = (typeof GalateaEntitlementSources)[number];

const stripePlanSchema = z.object({
  id: z.string(),
  product: z.union([z.string(), z.any()]).nullable(),
  interval: z.enum(['day', 'week', 'month', 'year']),
  interval_count: z.number(),
  amount: z.number().nullish(),
  currency: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullish(),
});
export type StripePlanSchema = z.infer<typeof stripePlanSchema>;

export const stripeSubscriptionScheduleSchema = z.object({
  id: z.string(),
  status: z.enum(['not_started', 'released', 'completed', 'active', 'canceled']),
  livemode: z.boolean(),
  metadata: z.record(z.string(), z.string()).nullable(),
  current_phase: z
    .object({
      start_date: z.number(),
      end_date: z.number(),
    })
    .nullable(),
  phases: z.array(
    z.object({
      start_date: z.number(),
      end_date: z.number(),
    }),
  ),
});
export type StripeSubscriptionScheduleSchema = z.infer<typeof stripeSubscriptionScheduleSchema>;

/** this is a partial schema for the stripe subscription object. Add more fields as needed. */
export const stripeSubscriptionSchema = z.object({
  id: z.string(),
  stripeAccountName: stripeAccountNameSchema,
  stripeCustomerId: z.string().nullable(),
  start_date: z.number().optional(),
  trial_start: z.number().nullish(),
  trial_end: z.number().nullish(),
  status: z.enum([
    'active',
    'canceled',
    'incomplete',
    'incomplete_expired',
    'past_due',
    'paused',
    'trialing',
    'unpaid',
  ]),
  metadata: z.record(z.string(), z.string()),
  cancel_at: z.number().nullable(),
  canceled_at: z.number().nullable(),
  livemode: z.boolean(),
  discounts: z.array(
    z.union([
      z.string(),
      z.object({
        id: z.string(),
        coupon: z.object({
          id: z.string(),
        }),
      }),
    ]),
  ),
  items: z.object({
    data: z.array(
      z.object({
        current_period_end: z.number(),
        current_period_start: z.number(),
        plan: stripePlanSchema,
      }),
    ),
  }),
  schedule: z.union([z.string(), stripeSubscriptionScheduleSchema]).nullable(),
});

export type StripeSubscriptionSchema = z.infer<typeof stripeSubscriptionSchema>;

const intervalSchema = z.enum(['weekly', 'monthly', 'quarterly', 'yearly', 'lifetime']).nullable();

const isUpgradeScheduledSchema = z.boolean().optional();

const baseEntitlementSchema = z.object({
  id: z.string(),
  productId: z.string(),
  status: z.enum(['active', 'expired', 'past_due', 'not_started']),
  interval: intervalSchema,
  entitlement: entitlementNameSchema,
  autoRenew: z.boolean(),
  startDate: z.string(),
  expirationDate: z.string(),
  isProduction: z.boolean(),
  managementUrl: z.string().nullish(),
  isFamilyPlan: z.boolean().optional(),
  familyPlanRole: z.enum(['manager', 'member']).optional(),
  isFreeTrialActive: z.boolean().nullable(),
  freeTrialEndsAt: z.string().nullable(),
  isUpgradeScheduled: isUpgradeScheduledSchema,
});

const stripeEntitlementSchema = baseEntitlementSchema.extend({
  source: z.literal('stripe'),
  stripeAccountName: stripeAccountNameSchema,
  stripeCustomerId: z.string().nullable(),
  plan: stripePlanSchema.optional(),
  hasUsedFreeTrial: z.boolean().nullable(),
  intervalPhases: z.array(intervalSchema).nullable(),
  schedule: stripeSubscriptionScheduleSchema.nullable(),
  discounts: z
    .array(
      z.string().or(
        z.object({
          id: z.string(),
          coupon: z.object({
            id: z.string(),
          }),
        }),
      ),
    )
    .nullish(),
  upgradeProductSku: z.string().optional(),
  sku: z.string().optional(),
  // refer to the docs for descriptions about these: https://docs.stripe.com/api/subscriptions/object#subscription_object-cancel_at
  // these lifecycle fields are in ISO string format
  canceledAt: z.string().nullable().optional(),
  cancelAt: z.string().nullable().optional(),
});
export type StripeEntitlementSchema = z.infer<typeof stripeEntitlementSchema>;

const revcatEntitlementSchema = baseEntitlementSchema.extend({
  source: z.literal('revcat'),
  // The RevenueCat store this subscription was purchased/billed through.
  //
  // Deliberately `z.string()` and NOT the raw vendor enum. `getCustomerSubscriptions` does not zod
  // validate the RevenueCat v2 response, so this value is an arbitrary runtime string by the time
  // Saturn copies it onto the entitlement. Two BFF routes serve `entitlementsInfoSchema` with
  // `responseValidation: true`, so a store value outside the enum (RevenueCat's `paddle`, for
  // example) would throw there and return a 500. The strict enum stays on the raw vendor schema,
  // where the shape is guaranteed. Consumers compare against known values and ignore the rest.
  //
  // Nullish so producers that predate this field still validate.
  store: z.string().nullish(),
  /**
   * Set when this entitlement is a promotional grant issued because of a partner-app
   * upsell purchase, rather than a real Galatea subscription or some other promotional grant.
   * Only ever set for Galatea's `unlimited` entitlement. `'candyjar'` is currently the only
   * partner app that grants this way.
   */
  grantedInPartnerApp: z.enum(['candyjar']).optional(),
});

const lifetimeEntitlementSchema = baseEntitlementSchema.extend({
  source: z.literal('lifetime'),
});

const customEntitlementSchema = baseEntitlementSchema.extend({
  source: z.literal('custom'),
  accessOrigin: z.literal('complimentary').optional(),
});

const paypalEntitlementSchema = baseEntitlementSchema.extend({
  source: z.literal('paypal'),
});

const revcatSimulatedEntitlementSchema = baseEntitlementSchema.extend({
  source: z.literal('revcat_simulated'),
});

export const entitlementSchema = z.discriminatedUnion('source', [
  stripeEntitlementSchema,
  revcatEntitlementSchema,
  lifetimeEntitlementSchema,
  customEntitlementSchema,
  paypalEntitlementSchema,
  revcatSimulatedEntitlementSchema,
]);

export const baseCachedValueSchema = z.object({
  /** Date ISO string */
  updatedAt: z.string(),
  /** The calling source which invoked the most recent update to the cache */
  updateCallSource: z.string(),
});

const baseCachedStripeSubscriptionSchedulesSchema = z.object({
  schedules: z.array(stripeSubscriptionScheduleSchema),
  stripeAccountName: stripeAccountNameSchema,
  stripeCustomerId: z.string().nullable(),
});
export type BaseCachedStripeSubscriptionSchedules = z.infer<typeof baseCachedStripeSubscriptionSchedulesSchema>;

export const cachedStripeSubscriptionSchedulesSchema = baseCachedValueSchema.extend({
  subscriptionSchedules: z.array(baseCachedStripeSubscriptionSchedulesSchema),
});
export type CachedStripeSubscriptionSchedules = z.infer<typeof cachedStripeSubscriptionSchedulesSchema>;

export const cachedStripeSubscriptionsSchema = baseCachedValueSchema.extend({
  subscriptions: z.array(stripeSubscriptionSchema),
});
export type CachedStripeSubscriptionSchema = z.infer<typeof cachedStripeSubscriptionsSchema>;

export const cachedPayPalSubscriptionsSchema = baseCachedValueSchema.extend({
  subscriptions: z.array(PayPalSubscription),
});
export type CachedPayPalSubscriptionSchema = z.infer<typeof cachedPayPalSubscriptionsSchema>;

export const cachedSaturnCustomSubscriptionsSchema = baseCachedValueSchema.extend({
  subscriptions: z.array(SaturnCustomSubscription),
});
export type CachedSaturnCustomSubscriptionSchema = z.infer<typeof cachedSaturnCustomSubscriptionsSchema>;

export const cachedJupiterCustomSubscriptionsSchema = baseCachedValueSchema.extend({
  subscriptions: z.array(JupiterCustomSubscription),
});
export type CachedJupiterCustomSubscriptionSchema = z.infer<typeof cachedJupiterCustomSubscriptionsSchema>;

export const cachedRevCatSubscriptionsSchema = baseCachedValueSchema.extend({
  subscriptions: z.array(revenueCatSubscriptionSchema),
});
export type CachedRevCatSubscriptionSchema = z.infer<typeof cachedRevCatSubscriptionsSchema>;

const debugSubscriptionDataSchema = z.object({
  // Debug data is intentionally not zod validated since it's intended to be used for debugging purposes ONLY
  stripe: z.array(z.unknown()).optional(),
  stripeSubscriptionSchedules: z.array(z.unknown()).optional(),
  paypal: z.array(z.unknown()).optional(),
  revcat: z.array(z.unknown()).optional(),
  custom: z.array(z.unknown()).optional(),
});

export const entitlementsInfoSchema = z.object({
  // !IMPORTANT: This schema must use explicit properties therefore DO NOT add any optional properties here (with the exception of `debugSubscriptionData`).
  // Use nullable properties for fields that can be null.
  activeUnlimitedEntitlementSources: z.array(z.enum(EntitlementSources)),
  hasActiveUnlimitedEntitlement: z.boolean(),
  willUnlimitedEntitlementRenew: z.boolean(),
  daysSinceUnlimitedExpiration: z.number().nullable(),
  entitlements: z.array(entitlementSchema),
  isCachedData: z.boolean(),
  /** ISO string */
  cacheUpdatedAt: z.string(),
  /** @deprecated - you can now access scheduled subscriptions directly from the `entitlements` array */
  scheduledSubscriptions: z.array(ScheduledSubscription),
  /**
   * @description should be strictly used for debugging purposes only. Should be NEVER used for any business logic.
   * The response type may change anytime and that should not break the previous version of the app.
   */
  debugSubscriptionData: debugSubscriptionDataSchema
    .extend({
      familyEntitlements: debugSubscriptionDataSchema.optional(),
    })
    .optional(),
});

export const productEntitlementsSchema = z.partialRecord(productNameSchema, entitlementsInfoSchema);

export type StripeEntitlement = z.infer<typeof stripeEntitlementSchema>;

export const isEntitlementName = (entitlement?: string): entitlement is EntitlementName =>
  typeof entitlement === 'string' && entitlementNames.includes(entitlement as EntitlementName);

export type Entitlement = z.infer<typeof entitlementSchema>;

export type EntitlementsInfo = z.infer<typeof entitlementsInfoSchema>;
export type ProductEntitlements = z.infer<typeof productEntitlementsSchema>;
export const GTV_UNLIMITED: EntitlementName = 'gtv_unlimited';
export const GTV_EARLY_ACCESS: EntitlementName = 'gtv_early_access';
